import { createTheme, type MantineThemeOverride } from '@mantine/core';

export const publicThemePalette = {
  darkBlue: '#1c376b',
  darkBlueHover: '#132547',
  logoBlue: '#16a8e0',
  lightGray: '#f3f6fb',
  highlightOne: '#29b6ef',
  highlightTwo: '#7176ff',
  textPrimary: '#0f1f3d',
  textSecondary: '#3a4e6c',
  textMuted: '#5f7393',
  border: '#d5dfee',
  borderStrong: '#bccbdf',
  surfaceMuted: '#e9f0fa',
  surfaceAlt: '#dde8f8',
  white: '#ffffff',
} as const;

export const publicCssVariables: Record<`--${string}`, string> = {
  '--surface-base': publicThemePalette.white,
  '--surface-soft': publicThemePalette.lightGray,
  '--surface-muted': publicThemePalette.surfaceMuted,
  '--surface-alt': publicThemePalette.surfaceAlt,
  '--surface-border': publicThemePalette.border,
  '--surface-border-strong': publicThemePalette.borderStrong,
  '--brand-primary': publicThemePalette.darkBlue,
  '--brand-primary-hover': publicThemePalette.darkBlueHover,
  '--brand-logo-blue': publicThemePalette.logoBlue,
  '--highlight-1': publicThemePalette.highlightOne,
  '--highlight-2': publicThemePalette.highlightTwo,
  '--highlight-1-soft': 'rgba(41, 182, 239, 0.28)',
  '--highlight-2-soft': 'rgba(113, 118, 255, 0.24)',
  '--text-primary': publicThemePalette.textPrimary,
  '--text-secondary': publicThemePalette.textSecondary,
  '--text-muted': publicThemePalette.textMuted,
};

const publicThemeConfig: MantineThemeOverride = {
  colors: {
    brand: [
      '#e8effa',
      '#d5e1f5',
      '#adc4eb',
      '#84a8e1',
      '#5f8dd8',
      '#3f74ce',
      '#2f5cb5',
      '#25498f',
      '#1c376b',
      '#132547',
    ],
    logo: [
      '#e6f7ff',
      '#c8ecfd',
      '#9fdefa',
      '#71cff7',
      '#47c1f3',
      '#29b6ef',
      '#16a8e0',
      '#0f8fbe',
      '#0f7297',
      '#105775',
    ],
    neutral: [
      '#f7f9fc',
      '#edf1f7',
      '#e1e8f2',
      '#d0d9e7',
      '#b5c2d6',
      '#96a6bf',
      '#7689a8',
      '#5d6f8d',
      '#455573',
      '#2f3d56',
    ],
    highlightA: [
      '#e7f8ff',
      '#c9efff',
      '#a4e4ff',
      '#79d8ff',
      '#50cbff',
      '#34bdf5',
      '#29b0e7',
      '#2198c8',
      '#1d7ba1',
      '#1b607d',
    ],
    highlightB: [
      '#f0f1ff',
      '#dfe2ff',
      '#c3c9ff',
      '#a4adff',
      '#8890ff',
      '#7176ff',
      '#5f60f5',
      '#4b49d9',
      '#3b39af',
      '#2f2f84',
    ],
  },
  primaryColor: 'brand',
  primaryShade: { light: 8, dark: 8 },
  defaultRadius: 'xl',
  autoContrast: true,
  fontFamily: "'Manrope', 'DM Sans', system-ui, -apple-system, sans-serif",
  headings: {
    fontFamily: "'Manrope', 'DM Sans', system-ui, -apple-system, sans-serif",
    fontWeight: '600',
  },
  components: {
    Button: {
      defaultProps: {
        radius: 'xl',
        size: 'sm',
        h: 44,
      },
      styles: {
        root: {
          fontWeight: 600,
          paddingInline: '16px',
        },
      },
    },
    Paper: {
      defaultProps: {
        radius: 'xl',
        withBorder: true,
      },
    },
    Accordion: {
      styles: {
        chevron: {
          color: 'var(--text-secondary)',
        },
      },
    },
    Title: {
      styles: {
        root: {
          letterSpacing: '-0.02em',
          color: 'var(--text-primary)',
        },
      },
    },
    Text: {
      styles: {
        root: {
          color: 'var(--text-secondary)',
        },
      },
    },
  },
};

export const publicTheme = createTheme(publicThemeConfig);
